import axiosInstance from "@/lib/axiosInstance";
import { formBuilderEndpoints } from "./formBuilder.endpoints";
import type {
  FormBuilderListItem,
  MarkFallbackDefaultResponse,
  SaveFormPayload,
} from "./formBuilder.types";
import type {
  AssignmentsAssistantRequest,
  AssignmentsAssistantResponse,
  AssistantRequest,
  AssistantResponse,
} from "./formAssistant.types";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

// The assistant panel shows its own errors inline, so the global alert stays quiet.
export const requestFormAssistant = async (
  payload: AssistantRequest,
  signal?: AbortSignal,
): Promise<AssistantResponse> => {
  const response = await axiosInstance.post(formBuilderEndpoints.assistant, payload, {
    signal,
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return response.data.data;
};

export const requestAssignmentsAssistant = async (
  payload: AssignmentsAssistantRequest,
  signal?: AbortSignal,
): Promise<AssignmentsAssistantResponse> => {
  const response = await axiosInstance.post(formBuilderEndpoints.assignmentsAssistant, payload, {
    signal,
    suppressErrorAlert: true,
  } as CustomAxiosRequestConfig);
  return response.data.data;
};

export const saveFormDetails = async (payload: SaveFormPayload): Promise<unknown> => {
  const response = await axiosInstance.put(formBuilderEndpoints.forms, payload);
  return response.data;
};

export const markFallbackDefault = async (formId: string): Promise<MarkFallbackDefaultResponse> => {
  const response = await axiosInstance.put(
    `${formBuilderEndpoints.forms}/${formId}/fallback-default`,
    {},
  );
  return response.data;
};

export interface FormLifecycleTarget {
  formId: string;
  tenantId?: string;
}

export interface DuplicateFormPayload extends FormLifecycleTarget {
  name: string;
}

const postFormAction = async (
  { formId, tenantId }: FormLifecycleTarget,
  action: "archive" | "unarchive" | "duplicate",
  body: Record<string, unknown> = {},
): Promise<FormBuilderListItem> => {
  const query = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";
  const url = `${formBuilderEndpoints.forms}/${encodeURIComponent(formId)}/${action}${query}`;
  const response = await axiosInstance.post(url, body);
  return response.data.data;
};

export const archiveForm = (target: FormLifecycleTarget) => postFormAction(target, "archive");

export const unarchiveForm = (target: FormLifecycleTarget) => postFormAction(target, "unarchive");

export const duplicateForm = ({ name, ...target }: DuplicateFormPayload) =>
  postFormAction(target, "duplicate", { name });
